import { isNotDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import clsx from 'clsx';
import type { ReactElement } from 'react';

import { useTargetAccumulatorSelector } from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import { ForbiddenExecutionTooltip } from '@/App/tiles/TargetAccumulator/TargetAccumulatorPriceSummary/Pricing/ForbiddenExecutionTooltip.tsx';
import { PricingDisplay } from '@/App/tiles/TargetAccumulator/TargetAccumulatorPriceSummary/Pricing/PricingDisplay.tsx';
import { useUserContext } from '@/context/UserContext';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { isPremiumAmountNegative } from '@/store/targetAccumulator/selectors.ts';
import { executeTargetAccumulatorThunk } from '@/store/targetAccumulator/thunk.ts';
import { getRateComponentStyling } from '@/utils/rateComponentStyling.ts';

interface PricingProps {
  tileId: string;
  isExecutable?: boolean;
}

export function Pricing({ tileId, isExecutable = false }: PricingProps): ReactElement {
  const dispatch = useAppDispatch();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const isTCCheckedStatus = useTargetAccumulatorSelector(
    (targetAccumulator) => targetAccumulator.tradeCapture.status === 'CHECKED',
  );
  const clientsWay = useTargetAccumulatorSelector((targetAccumulator) => targetAccumulator.properties.clientsWay);

  const { currentUser, tradePermission } = useUserContext();

  const isPremiumNegative = useTargetAccumulatorSelector(isPremiumAmountNegative);
  const isInternalSales = currentUser.userType === 'Internal Sales';
  const userEmail = currentUser.login;
  const canTrade = tradePermission === 'allowed' && !isPremiumNegative;
  const showColoredPrices = useAppSelector((state) => state.userPreferences.showColoredPrices);

  const handleExecution = () => {
    sgwtWebAnalytics?.trackEvent('Target Accumulator', 'Price double-clicked for execution');
    if (isNotDefined(tileId)) {
      return;
    }

    dispatch(executeTargetAccumulatorThunk({ tileId, userEmail }));
  };

  const isReadyExecutable = isExecutable && isTCCheckedStatus;

  return isExecutable ? (
    <ForbiddenExecutionTooltip placement="right" isNegativePremium={isPremiumNegative}>
      <div
        className={clsx(
          'price-pricing',
          'py-4px',
          'executable-pricing',
          { 'colored-prices': showColoredPrices },
          clientsWay === 'Buy' ? 'buy' : 'sell',
          ...getRateComponentStyling({ executable: isReadyExecutable, cantTrade: !canTrade }),
          { canTrade },
        )}
        onDoubleClick={canTrade && isReadyExecutable ? handleExecution : undefined}
        data-e2e="price-executable"
        data-e2e-rate-type={isReadyExecutable ? 'executable' : 'indicative'}
      >
        <PricingDisplay isInternalSales={isInternalSales} />
      </div>
    </ForbiddenExecutionTooltip>
  ) : (
    <div
      className={clsx(
        'price-pricing',
        'py-4px',
        'border-top',
        'border-bottom',
        'indicative-pricing',
        clientsWay === 'Buy' ? 'buy' : 'sell',
        { 'colored-prices': showColoredPrices },
        'bg-lvl2',
        { canTrade },
      )}
      data-e2e="price-indicative"
      data-e2e-rate-type={isReadyExecutable ? 'executable' : 'indicative'}
    >
      <PricingDisplay isInternalSales={isInternalSales} />
    </div>
  );
}
